const loadFreeSwatchesVue = () => {
  if (window.Vue) return Promise.resolve(window.Vue);
  if (window.freeSwatchesVuePromise) return window.freeSwatchesVuePromise;

  window.freeSwatchesVuePromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/vue@3.5.39/dist/vue.global.prod.js';
    script.async = true;
    script.onload = () => resolve(window.Vue);
    script.onerror = () => reject(new Error('Unable to load the design tool interface.'));
    document.head.appendChild(script);
  });

  return window.freeSwatchesVuePromise;
};

loadFreeSwatchesVue().then(({ createApp }) => {

createApp({
  data() {
    return {
      selectedSwatches: [],
      showSuccess: false,
      isSubmitting: false,
      showFilters: false,
      showCurtainOnly: false,
      lightboxVariant: null,
      currentImageIndex: 0,
      showSelectedPanel: false,
      quickFilter: [],
      quickFilters: [
        { id: 'light', label: 'Light Colors', colors: ['white', 'cream', 'beige', 'ivory', 'light'] },
        { id: 'white', label: 'White', colors: ['white', 'ivory', 'off-white'] },
        { id: 'beige', label: 'Beige/Khaki', colors: ['beige', 'khaki', 'tan', 'taupe'] },
        { id: 'gray', label: 'Gray', colors: ['gray', 'grey', 'charcoal'] },
        { id: 'dark', label: 'Dark Colors', colors: ['black', 'dark', 'navy', 'charcoal'] }
      ],
      filters: {
        features: [],
        color: [],
        texture: []
      },
      allProducts: [],
      isLoading: true
    };
  },
  computed: {
    filteredProducts() {
      const hiddenWithoutSwatchPhoto = ['laurel', 'ascent', 'brook', 'willow'];
      let filtered = this.allProducts.filter((product) => {
        const name = (product.productName || '').toLowerCase();
        return !hiddenWithoutSwatchPhoto.some((series) => name.startsWith(series));
      });
      
      // Quick filter - multiple selection
      if (this.quickFilter.length > 0) {
        filtered = filtered.filter(p => {
          const colorName = p.colorName.toLowerCase();
          return this.quickFilter.some(filterId => {
            const quickFilterConfig = this.quickFilters.find(f => f.id === filterId);
            return quickFilterConfig && quickFilterConfig.colors.some(color => colorName.includes(color));
          });
        });
      }
      
      if (this.filters.features.length > 0) {
        filtered = filtered.filter(p => {
          const tags = p.tags.toLowerCase();
          return this.filters.features.some(feature => tags.includes(feature.toLowerCase()));
        });
      }
      
      if (this.filters.color.length > 0) {
        filtered = filtered.filter(p => {
          const colorName = p.colorName.toLowerCase();
          return this.filters.color.some(color => colorName.includes(color.toLowerCase()));
        });
      }
      
      if (this.filters.texture.length > 0) {
        filtered = filtered.filter(p => {
          const tags = p.tags.toLowerCase();
          return this.filters.texture.some(texture => tags.includes(texture.toLowerCase()));
        });
      }
      
      if (this.showCurtainOnly) {
        filtered = filtered.filter(p => p.tags.toLowerCase().includes('curtain'));
      }
      
      return filtered;
    },
    
    groupedProducts() {
      const groups = {};
      
      this.filteredProducts.forEach(variant => {
        const key = variant.productId;
        if (!groups[key]) {
          groups[key] = {
            productId: variant.productId,
            productName: variant.productName,
            variants: []
          };
        }
        groups[key].variants.push(variant);
      });
      
      return Object.values(groups);
    },
    
    lightboxImages() {
      if (!this.lightboxVariant) return [];
      
      // If variant has gallery images, use them; otherwise use main image
      if (this.lightboxVariant.galleryImages && this.lightboxVariant.galleryImages.length > 0) {
        return this.lightboxVariant.galleryImages;
      }
      
      return [this.lightboxVariant.image];
    },
    
    flatVariants() {
      // Flatten all variants from grouped products
      return this.groupedProducts.flatMap(group => group.variants);
    },
    
    currentVariantIndex() {
      if (!this.lightboxVariant) return -1;
      return this.flatVariants.findIndex(v => v.id === this.lightboxVariant.id);
    }
  },
  methods: {
    getImageSrcset(imageUrl) {
      if (!imageUrl) return '';
      const base = imageUrl.split('?')[0];
      return `${base}?width=300 300w, ${base}?width=500 500w, ${base}?width=700 700w, ${base}?width=1000 1000w`;
    },
    
    getThumbnailSrcset(imageUrl) {
      if (!imageUrl) return '';
      const base = imageUrl.split('?')[0];
      return `${base}?width=80 80w, ${base}?width=100 100w, ${base}?width=150 150w`;
    },
    
    getProductUrl(productName) {
      // Convert product name to Shopify handle format
      // Remove special characters, convert to lowercase, replace spaces with hyphens
      const generatedHandle = productName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single
        .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens

      const productHandleOverrides = {
        'lodge-linen-curtains': 'lodge-custom-linen-curtains',
        'pearl-linen-curtains': 'pearl-custom-linen-curtains',
        'sanctuary-linen-curtains': 'sanctuary-custom-linen-curtains'
      };
      const handle = productHandleOverrides[generatedHandle] || generatedHandle;

      return `/products/${handle}`;
    },
    
    toggleQuickFilter(filterId) {
      const index = this.quickFilter.indexOf(filterId);
      if (index > -1) {
        this.quickFilter.splice(index, 1);
      } else {
        this.quickFilter.push(filterId);
      }
    },
    
    async fetchSwatchProducts() {
      this.isLoading = true;
      
      try {
        const productsData = window.swatchProductsData || [];
        this.allProducts = productsData;
        await this.loadCartSwatches();
      } catch (error) {
        console.error('Error loading swatch products:', error);
      } finally {
        this.isLoading = false;
      }
    },
    
    async loadCartSwatches() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        const cartVariantIds = cart.items.map(item => item.variant_id);
        const cartSwatches = this.allProducts.filter(variant => 
          cartVariantIds.includes(variant.id)
        );
        this.selectedSwatches = cartSwatches.slice(0, 10);
      } catch (error) {
        console.error('Error loading cart swatches:', error);
      }
    },
    
    isSelected(swatchId) {
      return this.selectedSwatches.some(s => s.id === swatchId);
    },
    
    toggleSwatch(swatch) {
      const isCurrentlySelected = this.isSelected(swatch.id);
      
      if (isCurrentlySelected) {
        this.removeSwatch(swatch.id);
      } else {
        if (this.selectedSwatches.length < 10) {
          this.selectedSwatches.push(swatch);
        }
      }
    },
    
    removeSwatch(swatchId) {
      this.selectedSwatches = this.selectedSwatches.filter(s => s.id !== swatchId);
    },
    
    openLightbox(variant) {
      this.lightboxVariant = variant;
      this.currentImageIndex = 0;
      document.body.style.overflow = 'hidden';
    },
    
    closeLightbox() {
      this.lightboxVariant = null;
      this.currentImageIndex = 0;
      document.body.style.overflow = '';
    },
    
    nextImage() {
      if (this.currentImageIndex < this.lightboxImages.length - 1) {
        this.currentImageIndex++;
      } else {
        this.currentImageIndex = 0;
      }
    },
    
    prevImage() {
      if (this.currentImageIndex > 0) {
        this.currentImageIndex--;
      } else {
        this.currentImageIndex = this.lightboxImages.length - 1;
      }
    },
    
    prevSwatch() {
      const variants = this.flatVariants;
      if (variants.length === 0) return;
      
      const currentIndex = this.currentVariantIndex;
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : variants.length - 1;
      
      this.lightboxVariant = variants[prevIndex];
      this.currentImageIndex = 0;
    },
    
    nextSwatch() {
      const variants = this.flatVariants;
      if (variants.length === 0) return;
      
      const currentIndex = this.currentVariantIndex;
      const nextIndex = currentIndex < variants.length - 1 ? currentIndex + 1 : 0;
      
      this.lightboxVariant = variants[nextIndex];
      this.currentImageIndex = 0;
    },
    
    applyFilters() {
      // Filters are automatically applied through computed property
    },
    
    toggleSelectedPanel() {
      this.showSelectedPanel = !this.showSelectedPanel;
    },
    
    async addToCart() {
      if (this.selectedSwatches.length === 0) return;
      
      this.isSubmitting = true;
      
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        const cartVariantIds = cart.items.map(item => item.variant_id);
        
        const newSwatches = this.selectedSwatches.filter(
          swatch => !cartVariantIds.includes(swatch.id)
        );
        
        if (newSwatches.length === 0) {
          alert('All selected swatches are already in your cart!');
          this.isSubmitting = false;
          return;
        }
        
        const items = newSwatches.map(swatch => ({
          id: swatch.id,
          quantity: 1
        }));
        
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });
        
        if (!response.ok) throw new Error('Failed to add to cart');
        
        this.showSuccess = true;
        setTimeout(() => { window.location.href = '/cart'; }, 1000);
        
        // Show success message
        this.showSuccess = true;
        
        // Redirect to cart after 1 second
        setTimeout(() => {
          window.location.href = '/cart';
        }, 1000);
        
      } catch (error) {
        console.error('❌ Error adding to cart:', error);
        alert('There was an error adding swatches to cart. Please try again.');
      } finally {
        this.isSubmitting = false;
      }
    },
    
    resetForm() {
      this.selectedSwatches = [];
      this.showSuccess = false;
    }
  },
  mounted() {
    this.fetchSwatchProducts();
    
    // Close lightbox on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.lightboxVariant) {
        this.closeLightbox();
      }
    });
  }
}).mount('#swatches-app');
}).catch((error) => {
  console.error('Unable to initialize the free swatches page:', error);
});
